import { Box, CircularProgress, FormLabel, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import * as React from 'react';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AttachmentInput } from '~/components/AttachmentInput';
import { EnumSelect } from '~/components/GenericForm/EnumSelect';
import { SalaryProposalProps } from '~/data/proposals';
import {
  BackDatingNotification,
  CommonEmployeeProposalFormHandle,
  CommonEmployeeProposalFormProps,
  ProposalDataFormButtons,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { ProposalResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalResponsibleHrSelect';
import { useProposalFormImperativeHandle } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalFormImperativeHandle';
import { HourlySalaryComponents } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/SalaryProposalForm/components/HourlySalaryComponents';
import { SalaryRaiseFields } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/SalaryProposalForm/components/SalaryRaiseFields';
import { useEmployeeDataForProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/useEmployeeDataForProposal';
import { SalaryProposalFormProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeSalaryProposalsLayout/useSalaryProposalsLayout';
import { convertDateTypeDatePickerProps } from '~/utils/helpers/date';

import { ScheduleEmploymentsDropdown } from '../Common/components/ScheduleEmploymentsDropdown';
import { useSxStyles } from '../Common/useStyles';

import { useSalaryProposalForm } from './useSalaryProposalForm';

type PropsType = CommonEmployeeProposalFormProps & {
  proposal: SalaryProposalProps;
  formProps: SalaryProposalFormProps;
};

export const SalaryProposalForm = React.forwardRef<
  CommonEmployeeProposalFormHandle,
  PropsType
>((props, ref) => {
  const { proposal, employeeId, isNewProposal, onProposalUpdate, formProps } =
    props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const { currency } = useEmployeeDataForProposal({ proposal });

  const {
    form,
    enums,
    responsibleHRSelector,
    handleFormSubmit,
    handleFormReset,
    currentEmploymentSalary,
    showHourlySalaryComponents,
    readOnlyMode,
    canSubmitForm,
    isLoading,
    fieldProps,
    attachmentUrl,
  } = useSalaryProposalForm({
    proposal,
    employeeId,
    onProposalUpdate,
    formProps,
  });

  useProposalFormImperativeHandle({ ref, handleFormSubmit });

  const salaryClassesEnums = React.useMemo(() => {
    return enums.salaryClasses.sort((a, b) => (a.seq || 1) - (b.seq || 1));
  }, [enums.salaryClasses]);

  const { control, formState, watch, getFieldState, setValue } = form;
  const getFieldStatus = useCallback(
    (fieldName) => {
      return {
        value: watch(fieldName),
        fieldState: getFieldState(fieldName),
      };
    },
    [getFieldState, watch],
  );
  const startDateField = getFieldStatus('startDate');
  const endDateField = getFieldStatus('endDate');

  if (
    isLoading.responsibleHR ||
    isLoading.applicationConfig ||
    isLoading.commonFieldProps
  ) {
    return <CircularProgress />;
  }

  return (
    <Box autoComplete="off" component="form" sx={sxStyles.form}>
      <Controller
        name={'employmentId'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <ScheduleEmploymentsDropdown
              value={value}
              onChange={onChange}
              error={error}
              readOnlyMode={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'salaryFrequency'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true }}
              value={value}
              onChange={onChange}
              enumOptions={enums.eventFrequencies}
              id={'hoursType'}
              label={t('laBaseSalaryFrequency')}
              required={true}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />

      {showHourlySalaryComponents ? (
        <HourlySalaryComponents
          form={form}
          currency={currency}
          hourlySalaryEnums={enums.hourlySalaryComponents}
          disabled={readOnlyMode}
          fieldProps={fieldProps}
        />
      ) : (
        <>
          <SalaryRaiseFields
            form={form}
            currency={currency}
            currentEmploymentSalary={currentEmploymentSalary}
            disabled={readOnlyMode}
          />

          <Box sx={sxStyles.fieldSet}>
            <FormLabel>{t('laPayPeriod')}</FormLabel>
            <Box sx={sxStyles.dateRange}>
              <Controller
                name={'startDate'}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <DatePicker
                      label={t('laStartDate')}
                      {...convertDateTypeDatePickerProps({
                        value,
                        onChange,
                        ...fieldProps.datePickers.startDate,
                      })}
                      slotProps={{
                        textField: {
                          required: true,
                          error: !!error?.message,
                          helperText: error?.message,
                        },
                      }}
                      disabled={readOnlyMode}
                    />
                  );
                }}
              />
              <Box>-</Box>
              <Controller
                name={'endDate'}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  return (
                    <DatePicker
                      label={t('laEndDate')}
                      {...convertDateTypeDatePickerProps({
                        value,
                        onChange,
                        ...fieldProps.datePickers.endDate,
                      })}
                      slotProps={{
                        textField: {
                          error: !!error?.message,
                          helperText: error?.message,
                        },
                      }}
                      disabled={readOnlyMode}
                    />
                  );
                }}
              />
            </Box>
            <BackDatingNotification
              enabled={
                startDateField.fieldState.isDirty ||
                endDateField.fieldState.isDirty
              }
              startDate={startDateField.value}
              endDate={endDateField.value}
            />
          </Box>
        </>
      )}

      <Controller
        name={'salaryType'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true }}
              value={value}
              onChange={onChange}
              enumOptions={enums.salaryTypes}
              includeEmptyOption={true}
              id={'salaryType'}
              label={t('laSalaryType')}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <Controller
        name={'salaryClass'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true }}
              value={value}
              onChange={onChange}
              enumOptions={salaryClassesEnums}
              includeEmptyOption={true}
              id={'salaryClass'}
              label={t('laSalaryClass')}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <Controller
        name={'changeReason'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true }}
              value={value}
              onChange={onChange}
              enumOptions={enums.changeReasons}
              id={'changeReason'}
              label={t('laRaiseReason')}
              required={true}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <Controller
        name={'info'}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField
              label={t('laInfo')}
              {...field}
              fullWidth={true}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <Controller
        name={'attachmentName'}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <AttachmentInput
              onChange={(selectedFile?: File) => {
                onChange(selectedFile?.name || '');
                setValue('attachment', selectedFile || null);
                setValue('attachmentId', -1);
              }}
              value={value}
              attachmentUrl={attachmentUrl}
              fullWidth={true}
              label={t('attachment')}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <ProposalResponsibleHRSelect
        form={form}
        control={control}
        selectorSettings={responsibleHRSelector}
        disabled={readOnlyMode}
      />
      <ProposalDataFormButtons
        isNewProposal={isNewProposal}
        onFormSubmitWithDecision={handleFormSubmit}
        onFormReset={handleFormReset}
        formState={formState}
        readOnlyMode={readOnlyMode}
        submitDecisionAllowed={canSubmitForm}
      />
    </Box>
  );
});
